import { IANAZone, Zone, type ZoneOffsetFormat, type ZoneOffsetOptions } from "luxon";
import { getTimezoneOffset, lookupZone } from "./timezone-data";

export class PackedZone extends Zone {
	readonly valid: boolean;
	private readonly zoneName: string;
	private readonly icu: IANAZone;

	constructor(name: string) {
		super();
		this.zoneName = name;
		this.valid = lookupZone(name) !== undefined;
		// The static factory is patched to create this class, so calling it here would recurse.
		this.icu = new IANAZone(name);
	}

	get type() {
		// Luxon sends iana, fixed, and system zones back through stale runtime Intl data.
		return "packed";
	}

	get name() {
		return this.zoneName;
	}

	get isUniversal() {
		return false;
	}

	get isValid() {
		return this.valid;
	}

	offset(timestampMs: number) {
		const offset = getTimezoneOffset({ instant: timestampMs, zoneName: this.zoneName });

		// Outside the artifact's coverage the runtime's rules are the better authority.
		return offset ?? this.icu.offset(timestampMs);
	}

	offsetName(timestampMs: number, options: ZoneOffsetOptions) {
		try {
			return this.icu.offsetName(timestampMs, options);
		} catch {
			// The artifact can ship a zone name the runtime has never heard of, and Intl throws on
			// those rather than degrading.
			return this.formatOffset(timestampMs, "short");
		}
	}

	formatOffset(timestampMs: number, format: ZoneOffsetFormat) {
		return Reflect.apply(this.icu.formatOffset, { offset: (value: number) => this.offset(value) }, [
			timestampMs,
			format,
		]);
	}

	equals(other: Zone) {
		return other instanceof PackedZone && other.name === this.zoneName;
	}
}
